/**
 * Pay & Contracts Page — top-level tabs for Earnings, Contracts, Expenses, Documents.
 * Shows tax refund card for selected year OR refund history for all years.
 */
import React, { useState, useEffect } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { PoundSterling, FolderOpen, Receipt, Briefcase, Wallet, CheckCircle2, Clock } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { useTaxYear } from '../context/TaxYearContext';
import { useAuth } from '../context/AuthContext';
import MonthlyEarnings from './MonthlyEarnings';
import Documents from './Documents';
import Expenses from './Expenses';
import ContractsSection from './monthly-earnings/ContractsSection';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const money = (v) => `£${Math.abs(parseFloat(v) || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}`;

/* Single-year refund card */
const RefundCard = ({ r }) => {
  const isRefund = r.refund_due > 0;
  const isActual = r.signed_off && r.actual_refund_received != null;
  const displayAmount = isActual ? r.actual_refund_received : r.refund_due;

  return (
    <Card className={`border ${
      displayAmount > 0 ? 'bg-emerald-500/5 border-emerald-500/20'
        : displayAmount < 0 ? 'bg-red-500/5 border-red-500/20'
          : 'bg-slate-800 border-slate-700'
    }`} data-testid="tax-refund-card">
      <CardContent className="p-4 sm:p-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className={`p-2.5 rounded-lg ${displayAmount > 0 ? 'bg-emerald-500/10' : 'bg-red-500/10'}`}>
              <Wallet className={`h-5 w-5 ${displayAmount > 0 ? 'text-emerald-400' : 'text-red-400'}`} />
            </div>
            <div>
              <p className="text-slate-400 text-sm">
                {isActual ? 'Actual Tax Refund' : 'Estimated Tax Refund'} — {r.tax_year}
              </p>
              <div className="flex items-baseline gap-2 mt-0.5">
                <span className={`text-2xl sm:text-3xl font-bold ${
                  displayAmount > 0 ? 'text-emerald-400' : displayAmount < 0 ? 'text-red-400' : 'text-slate-300'
                }`} data-testid="refund-amount">
                  {displayAmount >= 0 ? '' : '-'}{money(displayAmount)}
                </span>
                {isActual ? (
                  <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 text-[10px]" data-testid="refund-status-badge">
                    <CheckCircle2 className="h-3 w-3 mr-0.5" />Confirmed
                  </Badge>
                ) : (
                  <Badge className={`text-[10px] ${
                    r.status === 'final' ? 'bg-blue-500/20 text-blue-400 border-blue-500/30'
                      : 'bg-amber-500/20 text-amber-400 border-amber-500/30'
                  }`} data-testid="refund-status-badge">
                    <Clock className="h-3 w-3 mr-0.5" />{r.status === 'final' ? 'Awaiting Sign-off' : 'Estimate'}
                  </Badge>
                )}
              </div>
              <p className="text-xs text-slate-500 mt-1">
                {isActual
                  ? 'Your accountant has confirmed the final refund for this tax year'
                  : isRefund
                    ? 'Your accountant calculates you are due a refund'
                    : r.refund_due < 0
                      ? 'Your accountant calculates tax is still owed'
                      : 'Your tax position is balanced'}
                {r.sed_deduction > 0 && (
                  <span className="text-sky-400"> (includes SED of £{r.sed_deduction.toLocaleString()})</span>
                )}
              </p>
              {isActual && r.refund_due !== r.actual_refund_received && (
                <p className="text-[10px] text-slate-600 mt-0.5">
                  Original estimate: {money(r.refund_due)}
                </p>
              )}
            </div>
          </div>
          <div className="text-right text-xs text-slate-500 space-y-0.5">
            <p>Tax liability: {money(r.total_tax_liability)}</p>
            <p>Tax paid: {money(r.total_already_paid)}</p>
            {r.signed_off_at && <p>Signed off: {new Date(r.signed_off_at).toLocaleDateString('en-GB')}</p>}
            {!r.signed_off && r.last_calculated && (
              <p>Updated: {new Date(r.last_calculated).toLocaleDateString('en-GB')}</p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

/* All-years refund history */
const RefundHistory = ({ history }) => {
  if (!history || history.length === 0) return null;
  return (
    <Card className="bg-slate-800/50 border-slate-700" data-testid="refund-history-card">
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <Wallet className="h-4 w-4 text-amber-400" />
          <p className="text-white text-sm font-semibold">Tax Refund History</p>
        </div>
        <div className="space-y-2">
          {history.map((r) => {
            const isActual = r.signed_off && r.actual_refund_received != null;
            const displayAmount = isActual ? r.actual_refund_received : r.refund_due;
            return (
              <div key={r.tax_year} className={`flex items-center justify-between p-3 rounded-lg border ${
                isActual ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-slate-900/40 border-slate-700/50'
              }`} data-testid={`refund-year-${r.tax_year}`}>
                <div className="flex items-center gap-3">
                  <div>
                    <p className="text-white text-sm font-medium">{r.tax_year}</p>
                    <p className="text-[10px] text-slate-500">
                      {isActual ? 'Confirmed' : r.status === 'final' ? 'Awaiting sign-off' : 'Estimate'}
                    </p>
                  </div>
                  {isActual && <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" />}
                </div>
                <div className="text-right">
                  <p className={`text-sm font-bold ${displayAmount > 0 ? 'text-emerald-400' : displayAmount < 0 ? 'text-red-400' : 'text-slate-300'}`}>
                    {displayAmount >= 0 ? '' : '-'}{money(displayAmount)}
                  </p>
                  {isActual && r.refund_due !== r.actual_refund_received && (
                    <p className="text-[10px] text-slate-600">Est: {money(r.refund_due)}</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

const Earnings = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const { selectedYear } = useTaxYear();
  const { user } = useAuth();
  const [refundEstimate, setRefundEstimate] = useState(null);
  const [refundHistory, setRefundHistory] = useState(null);
  const isDocumentsPath = location.pathname === '/documents';
  const isExpensesPath = location.pathname === '/expenses';
  const tabParam = searchParams.get('tab');
  const initialTab = isDocumentsPath || tabParam === 'documents' ? 'documents'
    : isExpensesPath || tabParam === 'expenses' ? 'expenses'
    : tabParam === 'contracts' ? 'contracts'
    : 'earnings';
  const [activeTab, setActiveTab] = useState(initialTab);

  useEffect(() => {
    if (!user) return;
    const isAllYears = !selectedYear || selectedYear === 'all';

    if (isAllYears) {
      // Fetch full history
      const fetchHistory = async () => {
        try {
          const res = await axios.get(`${API_URL}/api/dashboard/tax-refund-history`);
          setRefundHistory(res.data?.years?.length > 0 ? res.data.years : null);
          setRefundEstimate(null);
        } catch (err) {
          // silent
          setRefundHistory(null);
        }
      };
      fetchHistory();
    } else {
      // Fetch single year
      const fetchRefund = async () => {
        try {
          const res = await axios.get(`${API_URL}/api/dashboard/tax-refund-estimate?tax_year=${selectedYear}`);
          if (res.data?.available) {
            setRefundEstimate(res.data);
          } else {
            // Fallback to previous year
            const baseYear = res.data?.tax_year || '';
            if (baseYear) {
              const parts = baseYear.split('/');
              const startYear = parseInt(parts[0]) - 1;
              const endShort = (startYear + 1).toString().slice(-2);
              const prevYear = `${startYear}/${endShort}`;
              const prevRes = await axios.get(`${API_URL}/api/dashboard/tax-refund-estimate?tax_year=${prevYear}`);
              setRefundEstimate(prevRes.data?.available ? prevRes.data : null);
            } else {
              setRefundEstimate(null);
            }
          }
          setRefundHistory(null);
        } catch (err) {
          // silent
          setRefundEstimate(null);
        }
      };
      fetchRefund();
    }
  }, [selectedYear, user]);

  const switchTab = (tab) => {
    setActiveTab(tab);
    setSearchParams(tab === 'earnings' ? {} : { tab });
  };

  const tabs = [
    { id: 'earnings', icon: PoundSterling, label: 'Earnings' },
    { id: 'contracts', icon: Briefcase, label: 'Contracts' },
    { id: 'expenses', icon: Receipt, label: 'Expenses' },
    { id: 'documents', icon: FolderOpen, label: 'Documents' },
  ];

  return (
    <div className="space-y-4" data-testid="earnings-page">
      {/* Single-Year Refund Card */}
      {refundEstimate?.available && <RefundCard r={refundEstimate} />}

      {/* All-Years History */}
      {refundHistory && <RefundHistory history={refundHistory} />}

      {/* Tab Header */}
      <div className="flex items-center gap-1 bg-slate-800/60 p-1 rounded-lg w-full sm:w-fit" data-testid="earnings-tabs">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => switchTab(tab.id)}
            className={`flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-2 rounded-md text-xs sm:text-sm font-medium transition-all whitespace-nowrap flex-1 sm:flex-none justify-center ${
              activeTab === tab.id
                ? 'bg-amber-600 text-white shadow-md shadow-amber-600/30'
                : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
            }`}
            data-testid={`${tab.id}-tab-btn`}
          >
            <tab.icon className="h-3.5 w-3.5 sm:h-4 sm:w-4 shrink-0" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'earnings' && <MonthlyEarnings />}
      {activeTab === 'contracts' && <ContractsSection />}
      {activeTab === 'documents' && <Documents />}
      {activeTab === 'expenses' && <Expenses />}
    </div>
  );
};

export default Earnings;
